import { ApiResponse } from "@/utils/api/ApiProps";
import api from "@/utils/api";
import Cookies from 'js-cookie';

export interface SchoolTeamMember {
  member_id: number;
  slate_id_fk: number;
  member_name: string;
  m_slug: string;
  member_qualification?: string;
  member_designation?: string;
  member_description?: string;
  member_image?: string;
  member_order?: number;
}

export interface CreateTeamMemberRequest {
  member_name: string;
  member_qualification?: string;
  member_designation?: string;
  member_description?: string;
  member_image?: string;
  member_order?: number;
}

export interface UpdateTeamMemberRequest extends CreateTeamMemberRequest {}

export interface UpdateMemberOrderRequest {
  members: Array<{
    member_id: number;
    member_order: number;
  }>;
}

// Get all team members for a school
export const getSchoolTeamMembers = async (
  slateId: string | number
): Promise<ApiResponse<{
  teamMembers: SchoolTeamMember[];
  count: number;
}>> => {
  return api.get(`/schools/${slateId}/team`);
};

// Get a specific team member
export const getTeamMemberById = async (
  slateId: string | number,
  memberId: number
): Promise<ApiResponse<SchoolTeamMember>> => {
  return api.get(`/schools/${slateId}/team/${memberId}`);
};

// Create a new team member
export const createTeamMember = async (
  slateId: string | number,
  data: CreateTeamMemberRequest
): Promise<ApiResponse<SchoolTeamMember>> => {
  return api.req<SchoolTeamMember>({
    url: `/schools/${slateId}/team`,
    method: api.Method.POST,
    body: data,
  });
};

// Update a team member
export const updateTeamMember = async (
  slateId: string | number,
  memberId: number,
  data: UpdateTeamMemberRequest
): Promise<ApiResponse<SchoolTeamMember>> => {
  return api.req<SchoolTeamMember>({
    url: `/schools/${slateId}/team/${memberId}`,
    method: api.Method.PUT,
    body: data,
  });
};

// Delete a team member
export const deleteTeamMember = async (
  slateId: string | number,
  memberId: number
): Promise<ApiResponse<null>> => {
  return api.req<null>({
    url: `/schools/${slateId}/team/${memberId}`,
    method: api.Method.DELETE,
  });
};

// Update team member order
export const updateMemberOrder = async (
  slateId: string | number,
  data: UpdateMemberOrderRequest
): Promise<ApiResponse<null>> => {
  return api.req<null>({
    url: `/schools/${slateId}/team/reorder`,
    method: api.Method.PUT,
    body: data,
  });
};

// Upload team member image
export const uploadMemberImage = async (
  slateId: string | number,
  file: File
): Promise<ApiResponse<{
  filename: string;
  url: string;
  message: string;
}>> => {
  const formData = new FormData();
  formData.append('image', file);

  // Get auth token from cookie (same as api-request.ts)
  const token = Cookies.get('crm_auth_token');

  // Use fetch directly for FormData upload
  const headers: Record<string, string> = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000/api'}/schools/${slateId}/team/upload-image`, {
    method: 'POST',
    headers,
    credentials: 'include', // Include cookies
    body: formData,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Failed to upload image');
  }

  return data;
};
